
import { apiClient } from "@/lib/api/client";

export interface Product {
  id: number;
  serial_no: string;
  lot_no: string;
  batch_no: number;
  product_name: string;
  quantity: number;
  expiration_date: string;
  status: "Good" | "Expired" | "Need to replenish";
  category: string;
  threshold: number;
  type?: string;
  is_expired: boolean;
  is_low_stock: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateProductData {
  product_name: string;
  quantity: number;
  expiration_date: string;
  category: string;
  threshold: number;
  type?: string;
  serial_no?: string;
  lot_no?: string;
}

export interface UpdateProductData {
  product_name?: string;
  quantity?: number;
  expiration_date?: string;
  category?: string;
  threshold?: number;
  type?: string;
  serial_no?: string;
  lot_no?: string;
}

export interface BulkReplenishResponse {
  success: boolean;
  message: string;
  data: {
    successful: Array<{
      id: number;
      product_name: string;
      old_quantity: number;
      quantity_added: number;
      new_quantity: number;
      old_status: string;
      new_status: string;
      is_now_good: boolean;
    }>;
    failed: Array<{
      id: number;
      error: string;
    }>;
    total_updated: number;
    total_quantity_added: number;
  };
}

export interface ProductStats {
  total_products: number;
  expired_products: number;
  low_stock_products: number;
  good_products: number;
  categories: Array<{ category: string; count: number }>;
}

export const productService = {
  async getProducts(params?: {
    search?: string;
    status?: string;
    category?: string;
    expired?: boolean;
    low_stock?: boolean;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    per_page?: number;
  }): Promise<{ data: Product[]; meta?: any }> {
    try {
      console.log("Sending params to /products:", params);
      const response = await apiClient.get("/products", { params });
      console.log("Products API Response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Products API Error Details:", {
        status: error.response?.status,
        data: error.response?.data,
        errors: error.response?.data?.errors,
        message: error.response?.data?.message,
      });
      throw new Error(
        error.response?.data?.message || "Failed to fetch products",
      );
    }
  },

  async getProduct(id: string | number): Promise<Product> {
    const response = await apiClient.get(`/products/${id}`);
    return response.data.data;
  },

  async getProductStats(): Promise<ProductStats> {
    try {
      console.log("Fetching product stats...");
      const response = await apiClient.get("/products/stats");
      console.log("Stats API Response:", response.data);

      // Check if response has data property
      if (response.data && typeof response.data === "object") {
        // Some APIs wrap data in a data property, others don't
        if (response.data.data !== undefined) {
          return response.data.data;
        } else if (response.data.success && response.data.data) {
          return response.data.data;
        } else {
          // Assume response.data is the stats object
          return response.data;
        }
      }

      // Return default if structure is unexpected
      return {
        total_products: 0,
        expired_products: 0,
        low_stock_products: 0,
        good_products: 0,
        categories: [],
      };
    } catch (error: any) {
      console.error("Get stats error:", error);
      console.error("Error response:", error.response?.data);

      // Return default stats on error
      return {
        total_products: 0,
        expired_products: 0,
        low_stock_products: 0,
        good_products: 0,
        categories: [],
      };
    }
  },

  async createProduct(
    data: CreateProductData,
  ): Promise<{ message: string; data: Product }> {
    try {
      console.log("Creating product with data:", data);
      const response = await apiClient.post("/products", data);
      console.log("Create product response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Create product error:", error.response?.data);
      if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors;
        const errorMessage = validationErrors
          ? Object.values(validationErrors).flat().join(", ")
          : "Validation failed";
        throw new Error(errorMessage);
      }
      throw new Error(
        error.response?.data?.message || "Failed to create product",
      );
    }
  },

  async updateProduct(
    id: string | number,
    data: UpdateProductData,
  ): Promise<{ message: string; data: Product }> {
    try {
      const response = await apiClient.put(`/products/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to update product",
      );
    }
  },

  async deleteProduct(id: string | number): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete(`/products/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to delete product",
      );
    }
  },
  

  async bulkReplenishStock(
    products: Array<{ id: number; quantity: number }>,
  ): Promise<BulkReplenishResponse> {
    try {
      console.log("Bulk replenish request:", { products });

      const response = await apiClient.post("/products/bulk-replenish", {
        products,
      });

      console.log("Bulk replenish response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Bulk replenish error:", error);
      console.error("Error details:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.response?.data?.message,
      });

      throw new Error(
        error.response?.data?.message || "Failed to replenish stock",
      );
    }
  },
  async replenishStock(
  productId: number,
  quantity: number,
): Promise<BulkReplenishResponse> {
  return this.bulkReplenishStock([
    {
      id: productId,
      quantity,
    },
  ]);
}
};